import { useState } from 'react';
import api from '@/lib/api';
import { useLinkChecks, LinkVerdict, LinkCheckSummary, LinkRecheckButton, PullTroubleNote } from '@/components/LinkCheck';
import { useConfirm } from '@/components/ConfirmDialog';
import { Pencil, X, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import { splitFootage } from '@/lib/tracking';

// SHIPPING TRACKING, in its own row (Ryan, 2026-09-29: tracking "should display somewhere else"): the tracking number
// and service the client gave, plus any tracking they put in a link box by mistake (lib/tracking.js). Never under the
// download links, never link-checked, never handed to Footage Pull.
function TrackingBlock({ prefix, number, service, entries, hardDrive }) {
  if (!hardDrive && !entries.length) return null;
  const hasNumber = !!(number && String(number).trim());
  return (
    <div className="mb-3" data-testid={`footage-${prefix}-tracking`}>
      <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-slate-500 block mb-1">Tracking</span>
      {hasNumber && (
        <p className="text-[11px] text-slate-400" data-testid={`footage-${prefix}-tracking-number`}>{number}{service ? ` (${service})` : ''}</p>
      )}
      {entries.map((t, i) => (/[./]/.test(t)
        ? <a key={i} href={t.startsWith('http') ? t : `https://${t}`} target="_blank" rel="noopener noreferrer"
             data-testid={`footage-${prefix}-tracking-link-${i}`}
             className="text-[11px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 break-all block">{t}</a>
        : <p key={i} className="text-[11px] text-slate-400" data-testid={`footage-${prefix}-tracking-entry-${i}`}>{t}</p>))}
      {!hasNumber && !entries.length && (
        <p className="text-[11px] italic text-slate-600" data-testid={`footage-${prefix}-tracking-pending`}>Hard drive, tracking info pending</p>
      )}
    </div>
  );
}

const CLOUD_UPLOAD = 'Cloud Upload (Dropbox/Drive/WeTransfer)';
const HARD_DRIVE = 'Hard Drive';

/**
 * Where the client's hard drive goes back to. Given on the order form, required by the server for
 * every hard-drive order (backend/app/return_address.py), and shown here to the team and to the client
 * whose address it is. Editors never receive the field. Projects ordered before the rule have none,
 * so the client or the team can add it here; the server refuses a blank one on a hard-drive project.
 */
function ReturnAddress({ project, canEdit, onRefresh, idSuffix = '' }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const address = (project.return_address || '').trim();

  const save = async () => {
    if (!val.trim()) { toast.error('Enter the return address for the hard drive'); return; }
    setBusy(true);
    try {
      await api.put(`/projects/${project.id}`, { return_address: val.trim() });
      if (onRefresh) onRefresh();
      toast.success('Return address saved');
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save the return address');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3" data-testid={`drawer-return-address${idSuffix}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-slate-500">Return Address</span>
        {canEdit && !editing && (
          <button type="button" data-testid={`drawer-return-address-edit-btn${idSuffix}`}
            onClick={() => { setVal(address); setEditing(true); }}
            className="tap-auto text-slate-500 hover:text-ug-gold transition-colors">
            <Pencil className="w-3 h-3" />
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-1.5">
          <textarea value={val} onChange={e => setVal(e.target.value)} rows={3} maxLength={500} autoFocus
            data-testid={`drawer-return-address-input${idSuffix}`}
            placeholder="Name, street, city, state, ZIP"
            className="w-full bg-ug-surface border border-ug-border text-zinc-100 rounded-md p-2 text-[11px] focus:border-ug-gold/40 outline-none resize-none placeholder:text-white/40" />
          <div className="flex gap-1.5 justify-end">
            <Button size="sm" disabled={busy} data-testid={`drawer-return-address-save-btn${idSuffix}`} onClick={save}
              className="bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 h-8 text-[9px] font-mono px-3">Save</Button>
            <Button size="sm" variant="ghost" data-testid={`drawer-return-address-cancel-btn${idSuffix}`} onClick={() => setEditing(false)}
              className="text-slate-500 h-8 text-[9px] font-mono px-2">Cancel</Button>
          </div>
        </div>
      ) : address ? (
        <p className="text-[11px] text-slate-300 whitespace-pre-wrap break-words" data-testid={`drawer-return-address-value${idSuffix}`}>{address}</p>
      ) : (
        <p className="text-[11px] italic text-slate-600" data-testid={`drawer-return-address-missing${idSuffix}`}>No return address given</p>
      )}
    </div>
  );
}

/**
 * A hard drive's steps, in one place: it arrives, then we upload it to our Dropbox and paste that link as "Our Dropbox
 * copy" (the field below). The copy is what moves the job: its link check passing moves it exactly as a client's
 * checked link does, and Footage Pull pulls it. "Loaded to NAS" and MediaShuttle were retired 2026-10-09 (Ryan:
 * "all that matters is that we have uploaded it to Dropbox, that is the important step after we receive a hard
 * drive"); shown until the copy is set.
 *
 * One component, rendered twice (video card, photo card) with a different `idSuffix`, because the
 * first cut of "mark received" was copied into both and they have to stay the same thing.
 */
function FootageReceipt({ project, onRefresh, idSuffix = '' }) {
  const [busy, setBusy] = useState(false);
  const received = !!project.footage_received;

  const setReceived = async (val) => {
    setBusy(true);
    try {
      await api.put(`/projects/${project.id}`, { footage_received: val });
      if (onRefresh) onRefresh();
      toast.success(val ? 'Footage marked received' : 'Footage receipt cleared');
    } catch { toast.error('Failed to update'); } finally { setBusy(false); }
  };

  return (
    <div className="mt-3" data-testid={`footage-received-control${idSuffix}`}>
      <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-slate-500 block mb-1">Footage</span>
      {!received && (
        <Button size="sm" disabled={busy} data-testid={`mark-footage-received-btn${idSuffix}`} onClick={() => setReceived(true)}
          className="tap-auto bg-oxidized-teal/20 text-oxidized-teal hover:bg-oxidized-teal/30 border border-oxidized-teal/30 h-8 text-[9px] font-mono px-3">
          Mark footage received
        </Button>
      )}
      {received && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 bg-amber-400/10 text-amber-300 border border-amber-400/30 font-sans text-[9px] font-medium px-2 py-1 rounded-full" data-testid={`footage-uploading-badge${idSuffix}`}>
            <Clock className="w-2.5 h-2.5" /> Uploading to Dropbox
          </span>
          <span className="text-[10px] text-slate-500">Paste the link in &ldquo;Our Dropbox copy&rdquo; when the upload finishes.</span>
          <button type="button" disabled={busy} data-testid={`unmark-footage-received-btn${idSuffix}`} onClick={() => setReceived(false)}
            className="text-[9px] text-slate-500 hover:text-velvet-burgundy font-mono transition-colors">Unmark</button>
        </div>
      )}
    </div>
  );
}

/**
 * "Our Dropbox copy" (R7, backend app/footage_copy.py and PUT /projects/{id}/footage-copy-link): footage that came on a
 * hard drive, or any way the pull tool cannot take, which the team put on the company Dropbox. One field for the team,
 * with the one sentence that says when to use it. Once saved it goes through the link check like any link, and once
 * that passes it is what the editor's Footage Pull and the prep computer download. The client's own links and details
 * above it stay exactly as they sent them, and the client never sees this field.
 */
function OurCopyLink({ project, linkRow, onRefresh }) {
  const current = (project.footage_copy_link || '').trim();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();

  const save = async (link) => {
    setBusy(true);
    try {
      await api.put(`/projects/${project.id}/footage-copy-link`, { link });
      toast.success(link ? 'Saved. The link is being checked now.' : 'Removed. The client\'s own links count again.');
      setEditing(false);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That did not save. Check the link and try again.');
    } finally { setBusy(false); }
  };
  const remove = async () => {
    const ok = await confirm({
      title: 'Remove our Dropbox copy?',
      message: 'The editor and auto-prep stop downloading from it, and the client\'s own links count again. The files on our Dropbox are not touched.',
      confirmLabel: 'Remove', cancelLabel: 'Keep it', destructive: true,
    });
    if (ok) save('');
  };

  return (
    <div className="mt-3" data-testid="footage-copy">
      <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-slate-500 block mb-1">Our Dropbox copy</span>
      <p className="text-[10px] text-slate-500 leading-relaxed mb-1.5" data-testid="footage-copy-when">
        Use this when the footage came on a hard drive, or any way other than Dropbox, Google Drive or WeTransfer: put it on
        our Dropbox and paste that folder&apos;s link here. It is checked like any link, then the editor and auto-prep download from it.
      </p>
      {editing ? (
        <div className="space-y-1.5">
          <Input value={val} onChange={e => setVal(e.target.value)} autoFocus data-testid="footage-copy-input"
            placeholder="https://www.dropbox.com/scl/fo/..." className="bg-ug-surface border-ug-border text-zinc-100 h-8 text-[11px]" />
          <div className="flex gap-1.5 justify-end">
            <Button size="sm" disabled={busy || !val.trim()} data-testid="footage-copy-save" onClick={() => save(val.trim())}
              className="bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 h-8 text-[9px] font-mono px-3">Save</Button>
            <Button size="sm" variant="ghost" data-testid="footage-copy-cancel" onClick={() => setEditing(false)}
              className="text-slate-500 h-8 text-[9px] font-mono px-2">Cancel</Button>
          </div>
        </div>
      ) : current ? (
        <div>
          <a href={current} target="_blank" rel="noopener noreferrer" data-testid="footage-copy-link"
            className="text-[11px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 break-all block">{current}</a>
          <LinkVerdict row={linkRow} role="staff" />
          {project.footage_copy_link_by_name && (
            <p className="text-[9px] text-slate-500 font-mono mt-0.5">
              {project.footage_copy_link_by_name}{project.footage_copy_link_at ? `, ${new Date(project.footage_copy_link_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
            </p>
          )}
          <div className="flex gap-3 mt-1">
            <button type="button" disabled={busy} data-testid="footage-copy-change" onClick={() => { setVal(current); setEditing(true); }}
              className="tap-auto text-[9px] text-slate-500 hover:text-ug-gold font-mono transition-colors">Change</button>
            <button type="button" disabled={busy} data-testid="footage-copy-remove" onClick={remove}
              className="tap-auto text-[9px] text-slate-500 hover:text-velvet-burgundy font-mono transition-colors">Remove</button>
          </div>
        </div>
      ) : (
        <Button size="sm" disabled={busy} data-testid="footage-copy-add" onClick={() => { setVal(''); setEditing(true); }}
          className="tap-auto bg-oxidized-teal/20 text-oxidized-teal hover:bg-oxidized-teal/30 border border-oxidized-teal/30 h-8 text-[9px] font-mono px-3">
          Paste our Dropbox link
        </Button>
      )}
    </div>
  );
}

/**
 * Canonical, read-write Footage Links block.
 * Single source of truth for the project Files tab — used by ProjectDrawer
 * and by the Redemption Cut Files tabs (both admin and editor pages).
 *
 * Editing (link list + upload status) only renders when `onRefresh` is provided
 * AND the viewing role is admin/staff/client (editors never edit links).
 * Redemption Files tabs pass no onRefresh → always read-only.
 */
export default function FootageLinks({ project, role, onRefresh }) {
  const { data: linkChecks, settled: linkChecksSettled, recheck: recheckLinks } = useLinkChecks(project?.id);
  const rowFor = (url) => (linkChecks?.rows || []).find(r => r.url === url);
  const [editingVideoLink, setEditingVideoLink] = useState(false);
  const [videoLinkVals, setVideoLinkVals] = useState(['']);


  if (!project) {
    return <p className="text-sm text-zinc-600 italic text-center py-8" data-testid="footage-links-empty">No project data</p>;
  }

  const isPhoto = project.project_type === 'photo';
  const canEdit = !!onRefresh && (role === 'admin' || role === 'staff' || role === 'client');
  const canMarkReceived = !!onRefresh && (role === 'admin' || role === 'staff');
  // The team and the client whose address it is; the server never sends it to anyone else.
  const showReturnAddress = role === 'admin' || role === 'staff' || role === 'client';
  // "Our Dropbox copy" is offered to the team where the footage did not come as a link the pull tool can take (a hard
  // drive, or any link it cannot take), and always once one is set, so it can be changed or removed.
  const copySet = !!(project.footage_copy_link || '').trim();
  const copyRow = rowFor((project.footage_copy_link || '').trim());
  const showCopy = canMarkReceived && (copySet
    || [project.video_transfer_method, project.photo_transfer_method].some(m => m && m !== CLOUD_UPLOAD)
    || (!!linkChecks && !(linkChecks.summary?.providers || []).some(p => ['dropbox', 'gdrive', 'wetransfer'].includes(p))));
  const labelLinks = role === 'client' ? 'Your Upload Links' : 'Alternate Download Link(s) From Client';

  const cardCls = 'rounded-xl bg-ug-raised border border-ug-border p-5';

  // Each side's link values, split into download links and tracking (lib/tracking.js, the server's rule too)
  const videoLinkList = (project.video_files_links || []).filter(Boolean);
  const videoSplit = splitFootage(videoLinkList.length > 0 ? videoLinkList : [project.video_files_link || project.files_link || '']);
  const photoLinkList = (project.photo_files_links || []).filter(Boolean);
  const photoSplit = splitFootage(photoLinkList.length > 0 ? photoLinkList : [project.photo_files_link || (isPhoto ? project.files_link : '') || '']);

  const renderVideoLinks = () => {
    // Split pasted multi-link fields the same way the backend's project_urls does, so each link gets its own line and
    // its own verdict; tracking a client put here is not a download link and shows in the Tracking row instead.
    const all = videoSplit.links;
    if (all.length > 0) {
      return (<>
        {all.map((l, i) => (
          <div key={i} className="mb-1">
            <a href={l.startsWith('http') ? l : `https://${l}`} target="_blank" rel="noopener noreferrer"
               data-testid={`footage-video-link-${i}`}
               className="text-[11px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 break-all block">{l}</a>
            <LinkVerdict row={rowFor(l)} role={role} />
          </div>
        ))}
        <LinkCheckSummary data={linkChecks} role={role} />
        <PullTroubleNote data={linkChecks} role={role} />
        <LinkRecheckButton data={linkChecks} settled={linkChecksSettled} role={role} onRecheck={recheckLinks} />
      </>);
    }
    if (project.video_transfer_method === 'Hard Drive') {
      // the drive's tracking is in the Tracking row; a download link appears here once the team adds one
      return <p className="text-[10px] text-slate-600 italic" data-testid="footage-video-hard-drive">No download link yet</p>;
    }
    if (project.video_will_update_later) {
      return <p className="text-[11px] text-slate-400 italic" data-testid="footage-video-will-update">Client will provide links later</p>;
    }
    return <p className="text-[10px] text-slate-600 italic" data-testid="footage-video-empty">No link provided</p>;
  };

  const renderPhotoLinks = () => {
    const all = photoSplit.links;   // same split as project_urls; tracking goes to the Tracking row
    if (all.length > 0) {
      // Each photo link carries its verdict too, as the video links do: a link that stopped working says so here
      // (the link watch, backend app/link_check/watch.py), whichever kind of footage it holds.
      return all.map((l, i) => (
        <div key={i} className="mb-1">
          <a href={l.startsWith('http') ? l : `https://${l}`} target="_blank" rel="noopener noreferrer"
             data-testid={`footage-photo-link-${i}`}
             className="text-[11px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 break-all block">{l}</a>
          <LinkVerdict row={rowFor(l)} role={role} />
        </div>
      ));
    }
    if (project.photo_transfer_method === 'Hard Drive') {
      return <p className="text-[10px] text-slate-600 italic" data-testid="footage-photo-hard-drive">No download link yet</p>;
    }
    if (project.photo_will_update_later) {
      return <p className="text-[11px] text-slate-400 italic" data-testid="footage-photo-will-update">Client will provide links later</p>;
    }
    return <p className="text-[10px] text-slate-600 italic" data-testid="footage-photo-empty">No link provided</p>;
  };

  return (
    <div className="flex flex-col gap-5" data-testid="footage-links">
      {/* Video Footage — shown for non-photo projects */}
      {!isPhoto && (
        <div className={cardCls} data-testid="footage-video-card">
          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold/60 block mb-4">Video Footage</span>

          <TrackingBlock prefix="video" number={project.video_tracking_number} service={project.video_shipping_service}
            entries={videoSplit.tracking} hardDrive={project.video_transfer_method === 'Hard Drive'} />

          <div>
            <div className="flex flex-wrap items-center gap-1.5 mb-1">
              <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-slate-500">{labelLinks}</span>
              {canEdit && (
                <button type="button" data-testid="edit-video-link-btn"
                  onClick={() => {
                    const existing = (project.video_files_links || []).filter(Boolean);
                    setVideoLinkVals(existing.length > 0 ? existing : [project.video_files_link || project.files_link || '']);
                    setEditingVideoLink(!editingVideoLink);
                  }}
                  className="text-slate-500 hover:text-ug-gold transition-colors">
                  <Pencil className="w-3 h-3" />
                </button>
              )}
              {project.video_transfer_method && <span className="font-mono text-[7px] text-slate-600 bg-white/[0.04] px-1.5 py-0.5 rounded ml-auto max-w-full">{project.video_transfer_method}</span>}
              {project.footage_size_gb && <span className="font-mono text-[7px] text-slate-600 bg-white/[0.04] px-1.5 py-0.5 rounded shrink-0 whitespace-nowrap">{project.footage_size_gb} GB</span>}
            </div>

            {editingVideoLink && canEdit ? (
              <div className="space-y-1.5">
                {videoLinkVals.map((val, i) => (
                  <div key={i} className="flex gap-1.5 items-center">
                    <Input value={val} onChange={e => { const next = [...videoLinkVals]; next[i] = e.target.value; setVideoLinkVals(next); }}
                      placeholder="https://drive.google.com/..." autoFocus={i === 0}
                      className="bg-ug-surface border-ug-border text-zinc-100 h-8 text-[11px] flex-1" />
                    {videoLinkVals.length > 1 && (
                      <button type="button" onClick={() => setVideoLinkVals(videoLinkVals.filter((_, j) => j !== i))}
                        className="text-slate-600 hover:text-velvet-burgundy transition-colors p-0.5"><X className="w-3 h-3" /></button>
                    )}
                  </div>
                ))}
                <div className="flex gap-2 items-center">
                  <button type="button" onClick={() => setVideoLinkVals([...videoLinkVals, ''])}
                    className="text-[9px] text-ug-gold/60 hover:text-ug-gold font-mono transition-colors">+ Add link</button>
                  <div className="flex gap-1.5 ml-auto">
                    <Button size="sm" data-testid="save-video-links-btn"
                      onClick={async () => {
                        const links = videoLinkVals.filter(l => l.trim());
                        try {
                          await api.put(`/projects/${project.id}`, { video_files_links: links, video_files_link: links[0] || '', files_link: links[0] || '', media_ready: true });
                          if (onRefresh) onRefresh();
                          toast.success('Links updated');
                          setEditingVideoLink(false);
                        } catch { toast.error('Failed to save links'); }
                      }}
                      className="bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 h-8 text-[9px] font-mono px-3">Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingVideoLink(false)}
                      className="text-slate-500 h-8 text-[9px] font-mono px-2">Cancel</Button>
                  </div>
                </div>
              </div>
            ) : (
              renderVideoLinks()
            )}

            {project.video_transfer_notes && !editingVideoLink && (
              <p className="text-[10px] text-slate-500 mt-2 italic whitespace-pre-wrap" data-testid="footage-video-notes">Notes: {project.video_transfer_notes}</p>
            )}
          </div>

          {project.video_transfer_method === 'Cloud Upload (Dropbox/Drive/WeTransfer)' && !editingVideoLink && (project.video_upload_status || canEdit) && (
            canEdit ? (
              <div className="mt-3">
                <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-slate-500 block mb-1">
                  Upload Status{role !== 'client' && <span className="text-slate-600 normal-case tracking-normal"> · editing on behalf of client</span>}
                </span>
                <Select value={project.video_upload_status || 'Not started'} onValueChange={async v => {
                  try {
                    await api.put(`/projects/${project.id}`, { video_upload_status: v, media_ready: v === 'Upload complete' });
                    if (onRefresh) onRefresh();
                    toast.success('Upload status updated');
                  } catch { toast.error('Failed to update status'); }
                }}>
                  <SelectTrigger data-testid="video-upload-status-select" className="h-8 bg-ug-surface border-ug-border text-xs w-48"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-ug-raised border-ug-border-strong">
                    <SelectItem value="Not started" className="text-zinc-100 text-xs">Not started</SelectItem>
                    <SelectItem value="Still uploading" className="text-zinc-100 text-xs">Still uploading</SelectItem>
                    <SelectItem value="Upload complete" className="text-zinc-100 text-xs">Upload complete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <p className="text-[9px] text-slate-500 mt-2" data-testid="footage-video-upload-status">Upload status: {project.video_upload_status}</p>
            )
          )}

          {showReturnAddress && project.video_transfer_method === HARD_DRIVE && (
            <ReturnAddress project={project} canEdit={canEdit} onRefresh={onRefresh} />
          )}

          {/* A drive: received, then uploading to our Dropbox until the team sets the copy (R7); then the copy is the footage. */}
          {canMarkReceived && project.video_transfer_method !== CLOUD_UPLOAD && !copySet && (
            <FootageReceipt project={project} onRefresh={onRefresh} />
          )}

          {showCopy && <OurCopyLink project={project} linkRow={copyRow} onRefresh={onRefresh} />}
        </div>
      )}

      {/* Photo Files — only on photo projects */}
      {isPhoto && (
        <div className={cardCls} data-testid="footage-photo-card">
          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold/60 block mb-4">Photo Files</span>

          <TrackingBlock prefix="photo" number={project.photo_tracking_number} service={project.photo_shipping_service}
            entries={photoSplit.tracking} hardDrive={project.photo_transfer_method === 'Hard Drive'} />

          <div>
            <div className="flex flex-wrap items-center gap-1.5 mb-1">
              <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-slate-500">{labelLinks}</span>
              {project.photo_transfer_method && <span className="font-mono text-[7px] text-slate-600 bg-white/[0.04] px-1.5 py-0.5 rounded ml-auto max-w-full">{project.photo_transfer_method}</span>}
            </div>
            {renderPhotoLinks()}
            {project.photo_transfer_notes && (
              <p className="text-[10px] text-slate-500 mt-2 italic whitespace-pre-wrap" data-testid="footage-photo-notes">Notes: {project.photo_transfer_notes}</p>
            )}
          </div>

          {project.photo_transfer_method === CLOUD_UPLOAD && (project.photo_upload_status || canEdit) && (
            canEdit ? (
              <div className="mt-3">
                <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-slate-500 block mb-1">
                  Upload Status{role !== 'client' && <span className="text-slate-600 normal-case tracking-normal"> · editing on behalf of client</span>}
                </span>
                <Select value={project.photo_upload_status || 'Not started'} onValueChange={async v => {
                  try {
                    await api.put(`/projects/${project.id}`, { photo_upload_status: v, media_ready: v === 'Upload complete' });
                    if (onRefresh) onRefresh();
                    toast.success('Upload status updated');
                  } catch { toast.error('Failed to update status'); }
                }}>
                  <SelectTrigger data-testid="photo-upload-status-select" className="h-8 bg-ug-surface border-ug-border text-xs w-48"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-ug-raised border-ug-border-strong">
                    <SelectItem value="Not started" className="text-zinc-100 text-xs">Not started</SelectItem>
                    <SelectItem value="Still uploading" className="text-zinc-100 text-xs">Still uploading</SelectItem>
                    <SelectItem value="Upload complete" className="text-zinc-100 text-xs">Upload complete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <p className="text-[9px] text-slate-500 mt-2" data-testid="footage-photo-upload-status">Upload status: {project.photo_upload_status}</p>
            )
          )}

          {showReturnAddress && project.photo_transfer_method === HARD_DRIVE && (
            <ReturnAddress project={project} canEdit={canEdit} onRefresh={onRefresh} idSuffix="-photo" />
          )}

          {canMarkReceived && project.photo_transfer_method !== CLOUD_UPLOAD && !copySet && (
            <FootageReceipt project={project} onRefresh={onRefresh} idSuffix="-photo" />
          )}

          {showCopy && <OurCopyLink project={project} linkRow={copyRow} onRefresh={onRefresh} />}
        </div>
      )}
    </div>
  );
}
